// 検索タグ API パス・パラメータ・レスポンス型
// 対応ルーター: backend/routers/tags.py

export const SearchTagApiPath = {
  LIST_CANDIDATES: '/api/sites/{site_id}/search-tags/candidates',
  CREATE_CANDIDATE: '/api/sites/{site_id}/search-tags/candidates',
  PREVIEW_CANDIDATE: '/api/sites/{site_id}/search-tags/candidates/{candidate_id}/preview',
  APPROVE_CANDIDATE: '/api/sites/{site_id}/search-tags/candidates/{candidate_id}/approve',
  REJECT_CANDIDATE: '/api/sites/{site_id}/search-tags/candidates/{candidate_id}/reject',
  HOLD_CANDIDATE: '/api/sites/{site_id}/search-tags/candidates/{candidate_id}/hold',
  EXTRACT_CANDIDATES: '/api/sites/{site_id}/search-tags/candidates/extract',
  LIST_TAGS: '/api/sites/{site_id}/search-tags',
  GET_TAG: '/api/sites/{site_id}/search-tags/{tag_id}',
  UPDATE_TAG: '/api/sites/{site_id}/search-tags/{tag_id}',
  REBUILD_TAG: '/api/sites/{site_id}/search-tags/{tag_id}/rebuild',
  ADD_SYNONYM: '/api/sites/{site_id}/search-tags/{tag_id}/synonyms',
  START_AB: '/api/sites/{site_id}/search-tags/{tag_id}/ab',
  STOP_AB: '/api/sites/{site_id}/search-tags/{tag_id}/ab/{test_id}/stop',
  DECIDE_AB: '/api/sites/{site_id}/search-tags/{tag_id}/ab/{test_id}/decide',
  DELETE_SYNONYM: '/api/sites/{site_id}/search-tags/{tag_id}/synonyms/{raw_value}',
  GET_DASHBOARD: '/api/sites/{site_id}/search-tags-dashboard',
  LIST_OPTIMIZATIONS: '/api/sites/{site_id}/search-tags-optimizations',
  RUN_OPTIMIZATIONS: '/api/sites/{site_id}/search-tags-optimizations/run',
  APPLY_OPTIMIZATION: '/api/sites/{site_id}/search-tags-optimizations/{optimization_id}/apply',
  DISMISS_OPTIMIZATION:
    '/api/sites/{site_id}/search-tags-optimizations/{optimization_id}/dismiss',
  GET_DELIVERY: '/api/sites/{site_id}/search-tags-delivery',
  UPDATE_DELIVERY: '/api/sites/{site_id}/search-tags-delivery',
  ISSUE_DELIVERY_TOKEN: '/api/sites/{site_id}/search-tags-delivery/token',
  GET_SETTINGS: '/api/sites/{site_id}/search-tags-settings',
  UPDATE_SETTINGS: '/api/sites/{site_id}/search-tags-settings'
} as const

export type TagCandidateStatus = 'pending' | 'approved' | 'rejected' | 'held'

export interface TagCandidate {
  id: number
  word: string
  source: string
  search_freq: number
  product_count: number
  /** 承認前の試算（意味検索の在庫あり件数）。null は未算出で、0 とは区別する */
  preview_population: number | null
  score: number
  status: TagCandidateStatus
  source_ref_type: string | null
  source_ref_id: string | null
}

export interface SearchTag {
  id: number
  tag_name: string
  tag_slug: string
  tag_type: string
  status: string
  pinned: boolean
  created_from: string | null
  merged_into: number | null
  population: number
  in_stock_population: number | null
  created_at: string | null
}

export interface SearchTagProduct {
  product_id: string
  relevance: number
  name: string | null
  image_url: string | null
  in_stock: boolean
}

// --- 承認キュー ---

export interface ListTagCandidatesVars {
  site_id: number
  status?: TagCandidateStatus
  source?: string
  search?: string
  page?: number
  per_page?: number
}

export interface ListTagCandidatesResponse {
  items: TagCandidate[]
  total: number
  page: number
  per_page: number
  counts_by_status: Record<string, number>
}

export interface CreateTagCandidateVars {
  site_id: number
  word: string
}

export interface CandidateActionVars {
  site_id: number
  candidate_id: number
}

export interface PreviewCandidateResponse {
  candidate_id: number
  preview_population: number | null
  /** 意味検索が使えるか。false のとき preview_population は null */
  measurable: boolean
}

export interface ApproveCandidateVars extends CandidateActionVars {
  tag_name?: string | null
  pinned?: boolean
}

export interface ExtractCandidatesVars {
  site_id: number
}

export interface ExtractCandidatesResponse {
  site_id: number
  source_search: number
  source_product_text: number
  source_structured: number
  merged: number
  created: number
  updated: number
  skipped_decided: number
}

// --- タグ一覧・詳細 ---

export interface ListTagsVars {
  site_id: number
  status?: string
  tag_type?: string
  search?: string
  page?: number
  per_page?: number
}

export interface ListTagsResponse {
  items: SearchTag[]
  total: number
  page: number
  per_page: number
}

export interface GetTagVars {
  site_id: number
  tag_id: number
}

export type TagAbVariant = 'control' | 'variant'

export interface TagAbSide {
  name: string
  impressions: number
  clicks: number
  page_views: number
  conversions: number
  /** 分母が0なら null（0% と区別する） */
  ctr: number | null
}

export interface TagAbTest {
  id: number
  tag_id: number
  status: string
  start_date: string
  end_date: string | null
  winner: TagAbVariant | null
  control: TagAbSide
  variant: TagAbSide
  /** 表示が足りているか。足りないうちは勝敗を出さない */
  enough_data: boolean
  min_impressions_per_side: number
  leading: TagAbVariant | null
  today_variant: TagAbVariant
  elapsed_days: number
}

export interface StartTagAbVars extends GetTagVars {
  variant_name: string
}

export interface TagAbActionVars extends GetTagVars {
  test_id: number
}

export interface DecideTagAbVars extends TagAbActionVars {
  winner: TagAbVariant
}

export interface TagArticleLink {
  articleId: string
  title: string
  url: string
  description: string
  relevance: number
}

export interface TagDetailResponse {
  tag: SearchTag
  synonyms: string[]
  products: SearchTagProduct[]
  band_min: number
  band_max: number
  converged: boolean
  note: string
  /** 実行中・直近の文言A/B（無ければ null） */
  ab: TagAbTest | null
  /** タグに紐づく公開中の記事 */
  articles: TagArticleLink[]
}

export interface UpdateTagVars extends GetTagVars {
  tag_name?: string
  pinned?: boolean
  status?: string
  merged_into?: number
}

export interface RebuildTagResponse {
  population: number
  in_stock: number
  cutoff: number | null
  converged: boolean
  reason: string
}

export interface AddSynonymVars extends GetTagVars {
  raw_value: string
}

export interface DeleteSynonymVars extends GetTagVars {
  raw_value: string
}

// --- 設定 ---

export interface GetTagSettingsVars {
  site_id: number
}

export interface TagSettingsResponse {
  population_min: number
  population_max: number
  product_tag_min: number
  product_tag_max: number
  min_impressions: number
  low_ctr_ratio: number
  batch_frequency: string
  auto_apply: boolean
}

export interface UpdateTagSettingsVars extends GetTagSettingsVars {
  population_min?: number
  population_max?: number
  product_tag_min?: number
  product_tag_max?: number
  min_impressions?: number
  low_ctr_ratio?: number
  batch_frequency?: string
  auto_apply?: boolean
}

// --- ダッシュボード ---

export type MeasurementStatus = 'active' | 'stale' | 'no_data'

export interface TagKpis {
  /** 表示されたタグの延べ数（イベント数。tag_metrics の合計と一致する） */
  tag_impressions: number
  /** 枠は出たがタグが0件だった回数（設置済みなのに出ていない、の検知用） */
  tag_frame_views: number
  /** 分母が0なら null（0% と区別する） */
  tag_ctr: number | null
  tag_clicks: number
  tag_page_views: number
  tag_page_ctr: number | null
  product_clicks: number
  cv_count: number
  cvr: number | null
  cvr_denominator: number
  gmv: number
  measurement_status: MeasurementStatus
  last_event_at: string | null
  events_in_period: number
}

export interface TagFunnelStep {
  key: string
  label: string
  /** 未計測は null（0 と区別する） */
  count: number | null
  rate_from_prev: number | null
}

export interface TagPerformance {
  tag_id: number
  tag_name: string
  tag_slug: string
  impressions: number
  clicks: number
  ctr: number | null
  page_views: number
  conversions: number
}

export interface GetTagDashboardVars {
  site_id: number
  days?: number
}

export interface TagDashboardResponse {
  period_days: number
  kpis: TagKpis
  funnel: TagFunnelStep[]
  tags: TagPerformance[]
}

// --- 最適化提案 ---

export type TagOptimizationKind = 'band_deviation' | 'low_ctr' | 'no_traffic' | 'thin_tagging'
export type TagOptimizationAction = 'rebuild' | 'merge' | 'rename' | 'noindex' | 'extract'
export type TagOptimizationStatus = 'pending' | 'applied' | 'dismissed'

export interface TagOptimization {
  id: number
  /** サイト全体の提案では null */
  tag_id: number | null
  tag_name: string | null
  kind: TagOptimizationKind
  action: TagOptimizationAction
  status: TagOptimizationStatus
  summary: string
  detail: Record<string, unknown>
  created_at: string | null
}

export interface ListTagOptimizationsVars {
  site_id: number
  status?: TagOptimizationStatus
}

export interface ListTagOptimizationsResponse {
  items: TagOptimization[]
  counts_by_status: Record<string, number>
  /** ON でも自動適用されるのは母集団再構築のみ */
  auto_apply: boolean
  batch_frequency: string
}

export interface RunTagOptimizationsVars {
  site_id: number
}

export interface RunTagOptimizationsResponse {
  band_deviation: number
  low_ctr: number
  no_traffic: number
  thin_tagging: number
  created: number
  updated: number
}

export interface TagOptimizationActionVars {
  site_id: number
  optimization_id: number
}

export interface TagOptimizationActionResponse {
  id: number
  status: string
  action: TagOptimizationAction
  result: Record<string, unknown>
}

// --- タグ配信 ---

export interface TagEndpointStatus {
  key: string
  label: string
  path: string
  method: string
  /** 未受信は null（「受信0件」ではなく「まだ届いていない」） */
  last_request_at: string | null
}

export interface TagDeliveryResponse {
  /** sites.enable_tag_pages。ここでは表示のみ（切替はサイト管理） */
  site_enabled: boolean
  nununi_id: string | null
  api_base_url: string
  enabled: boolean
  token_issued: boolean
  /** 平文は保存しないため末尾4文字のみ */
  token_hint: string | null
  tag_page_url_format: string
  tag_page_url_example: string
  endpoints: TagEndpointStatus[]
  last_request_at: string | null
  measurement_status: MeasurementStatus
  measurement_last_event_at: string | null
  embed_snippet: string
  cv_snippet: string
}

export interface GetTagDeliveryVars {
  site_id: number
}

export interface UpdateTagDeliveryVars extends GetTagDeliveryVars {
  enabled?: boolean
  tag_page_url_format?: string
}

export interface IssueTagTokenResponse {
  nununi_id: string
  /** この応答でしか受け取れない平文トークン */
  api_token: string
  token_hint: string
  reissued: boolean
}
